import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, API } from "@/lib/api";
import { can, rupees, fmtDate } from "@/lib/helpers";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Coins, WhatsappLogo, HandCoins, Trash } from "@phosphor-icons/react";

const TYPE_LABEL = { monthly: "Monthly", eleven_plus_one: "11 + 1", fixed_amount: "Fixed Amount", weight_based: "Weight Based" };

export default function Schemes() {
  const { user, activeBranch } = useAuth();
  const [schemes, setSchemes] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [schemeOpen, setSchemeOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [payDate, setPayDate] = useState("");
  const [enrollForm, setEnrollForm] = useState({ scheme_id: "", customer_id: "", start_date: "", agent_code: "", referral_percent: "" });
  const [schemeForm, setSchemeForm] = useState({ name: "", scheme_type: "monthly", duration_months: 12, installment_amount: 5000, bonus_installments: 0, description: "" });
  const canCreate = can(user, "schemes", "create");
  const canEnroll = can(user, "schemes", "enroll");
  const canCollect = can(user, "schemes", "collect");
  const canDelete = can(user, "schemes", "delete");
  const canEditScheme = can(user, "schemes", "edit");

  async function openPdf(path) {
    const token = localStorage.getItem("og_token");
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Could not open PDF"); return; }
    window.open(URL.createObjectURL(await res.blob()), "_blank");
  }

  const uploadSigned = async (file) => {
    if (!file) return;
    const token = localStorage.getItem("og_token");
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await fetch(`${API}/schemes/enrollments/${detail.id}/signed-acceptance`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Upload failed"); }
      toast.success("Signed acceptance uploaded"); openDetail(detail.id);
    } catch (e) { toast.error(e.message); }
  };

  const load = async () => {
    setSchemes((await api.get("/schemes")).data);
    setEnrollments((await api.get("/schemes/enrollments")).data);
  };
  useEffect(() => { load(); api.get("/customers").then(({ data }) => setCustomers(data)); api.get("/agents/names").then(({ data }) => setAgents(data)).catch(() => setAgents([])); }, []);

  const createScheme = async () => {
    try {
      await api.post("/schemes", { ...schemeForm, duration_months: Number(schemeForm.duration_months), installment_amount: Number(schemeForm.installment_amount), bonus_installments: Number(schemeForm.bonus_installments) });
      toast.success("Scheme created"); setSchemeOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doEnroll = async () => {
    try {
      if (!enrollForm.start_date) return toast.error("Scheme start date is required");
      const body = { scheme_id: enrollForm.scheme_id, customer_id: enrollForm.customer_id, branch_id: activeBranch };
      if (enrollForm.start_date) body.start_date = new Date(enrollForm.start_date).toISOString();
      if (enrollForm.agent_code && enrollForm.agent_code.trim()) body.agent_code = enrollForm.agent_code.trim();
      if (enrollForm.referral_percent !== "") body.referral_percent = Number(enrollForm.referral_percent);
      const { data } = await api.post("/schemes/enroll", body);
      toast.success(`Enrolled — ${data.account_no}`); setEnrollOpen(false); setEnrollForm({ scheme_id: "", customer_id: "", start_date: "", agent_code: "", referral_percent: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteScheme = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this scheme master?")) return;
    try { await api.delete(`/schemes/${id}`); toast.success("Scheme deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const postpone = async () => {
    if (!window.confirm("Postpone all pending installments by 1 month?")) return;
    try { await api.post(`/schemes/enrollments/${detail.id}/postpone`, { months: 1, reason: "missed installment" }); toast.success("Maturity postponed by 1 month"); openDetail(detail.id); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/schemes/enrollments/${id}`);
    setDetail(data); setPayAmt(String(data.installment_amount));
  };

  const pay = async () => {
    try {
      const body = { amount: Number(payAmt), mode: "cash" };
      if (payDate) body.paid_on = new Date(payDate).toISOString();
      await api.post(`/schemes/enrollments/${detail.id}/pay`, body);
      toast.success("Installment collected"); openDetail(detail.id); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const close = async (action) => {
    try {
      await api.post(`/schemes/enrollments/${detail.id}/close`, { action });
      toast.success(`Scheme ${action}${action === "redeem" ? "ed" : "led"}`); setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remind = async (id) => {
    try {
      const { data } = await api.post(`/schemes/enrollments/${id}/remind`);
      if (data.link) window.open(data.link, "_blank");
      else toast.success("WhatsApp reminder sent");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Gold Schemes" subtitle="Savings plans, installment collection, maturity & redemption.">
        {canCreate && <Button variant="outline" onClick={() => setSchemeOpen(true)} className="border-[#E6E2D6]" data-testid="add-scheme-btn"><Plus size={16} className="mr-1" /> New Scheme</Button>}
        {canEnroll && <Button onClick={() => setEnrollOpen(true)} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="enroll-btn"><Coins size={16} className="mr-1" /> Enroll Customer</Button>}
      </PageHeader>

      <Tabs defaultValue="enrollments">
        <TabsList className="bg-[#F2EBE1]">
          <TabsTrigger value="enrollments" data-testid="sch-tab-enroll">Enrollments</TabsTrigger>
          <TabsTrigger value="schemes" data-testid="sch-tab-master">Scheme Master</TabsTrigger>
        </TabsList>

        <TabsContent value="enrollments" className="mt-4">
          <Card className="bg-white border-[#E6E2D6] shadow-sm">
            {enrollments.length === 0 ? <EmptyState label="No enrollments yet" /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Account</TableHead><TableHead>Customer</TableHead><TableHead>Scheme</TableHead><TableHead>Progress</TableHead><TableHead className="text-right">Paid</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {enrollments.map((e) => (
                    <TableRow key={e.id} className="cursor-pointer" onClick={() => openDetail(e.id)} data-testid={`enroll-row-${e.account_no}`}>
                      <TableCell className="font-medium">{e.account_no}</TableCell>
                      <TableCell>{e.customer_name}</TableCell>
                      <TableCell className="text-sm">{e.scheme_name}</TableCell>
                      <TableCell className="w-40">
                        <div className="flex items-center gap-2">
                          <Progress value={(e.paid_count / (e.payable_installments || e.duration_months)) * 100} className="h-2" />
                          <span className="text-xs text-[#8A8A8A] whitespace-nowrap">{e.paid_count}/{e.payable_installments || e.duration_months}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{rupees(e.total_paid)}</TableCell>
                      <TableCell><StatusBadge status={e.status} /></TableCell>
                      <TableCell onClick={(ev) => ev.stopPropagation()}>
                        {canCollect && e.status === "active" && <Button size="icon" variant="ghost" onClick={() => remind(e.id)} title="Send reminder" data-testid={`remind-${e.account_no}`}><WhatsappLogo size={16} className="text-[#25D366]" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="schemes" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {schemes.map((s) => (
              <Card key={s.id} className="p-5 bg-white border-[#E6E2D6] shadow-sm relative" data-testid={`scheme-card-${s.id}`}>
                {canDelete && <button onClick={(e) => deleteScheme(s.id, e)} className="absolute top-3 right-3 text-[#9D2A2A] hover:opacity-70" data-testid={`delete-scheme-${s.id}`}><Trash size={15} /></button>}
                <Badge className="bg-[#F2EBE1] text-[#8A6D2F] border-0 mb-2">{TYPE_LABEL[s.scheme_type]}</Badge>
                <div className="font-display text-xl font-semibold text-[#1A1A1A]">{s.name}</div>
                <p className="text-xs text-[#8A8A8A] mt-1 h-8">{s.description}</p>
                <div className="mt-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-[#8A8A8A]">Installment</span><span className="tabular-nums">{rupees(s.installment_amount)}</span></div>
                  <div className="flex justify-between"><span className="text-[#8A8A8A]">Duration</span><span>{s.duration_months} months</span></div>
                  {s.bonus_installments > 0 && <div className="flex justify-between"><span className="text-[#8A8A8A]">Bonus</span><span className="text-[#2D6A4F]">+{s.bonus_installments}</span></div>}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Enroll dialog */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Enroll Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Scheme</Label>
              <Select value={enrollForm.scheme_id} onValueChange={(v) => setEnrollForm({ ...enrollForm, scheme_id: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="enroll-scheme"><SelectValue placeholder="Select scheme" /></SelectTrigger>
                <SelectContent className="bg-white">{schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Customer</Label>
              <Select value={enrollForm.customer_id} onValueChange={(v) => setEnrollForm({ ...enrollForm, customer_id: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="enroll-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent className="bg-white max-h-56">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Scheme Start Date <span className="text-[#9D2A2A]">*</span> (back/future dated allowed)</Label>
              <Input type="date" value={enrollForm.start_date} onChange={(e) => setEnrollForm({ ...enrollForm, start_date: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="enroll-start-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B6B6B]">Referral Agent (if referred)</Label>
                <Select value={enrollForm.agent_code || "none"} onValueChange={(v) => setEnrollForm({ ...enrollForm, agent_code: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="enroll-agent-code"><SelectValue placeholder="Select agent" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-56">
                    <SelectItem value="none">No agent</SelectItem>
                    {agents.map((a) => <SelectItem key={a.id} value={a.agent_code}>{a.name} ({a.agent_code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B6B6B]">Referral % (optional)</Label>
                <Input type="number" step="0.1" value={enrollForm.referral_percent} onChange={(e) => setEnrollForm({ ...enrollForm, referral_percent: e.target.value })} placeholder="agent default" className="mt-1 bg-white border-[#E6E2D6]" data-testid="enroll-referral-pct" />
              </div>
            </div>
            <p className="text-[11px] text-[#8A8A8A] -mt-1">If an agent code is entered, that agent earns the referral % on every installment collected for this scheme.</p>
          </div>
          <DialogFooter><Button onClick={doEnroll} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="enroll-save">Enroll</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New scheme dialog */}
      <Dialog open={schemeOpen} onOpenChange={setSchemeOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Scheme</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">Name</Label><Input value={schemeForm.name} onChange={(e) => setSchemeForm({ ...schemeForm, name: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="sf-name" /></div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Type</Label>
              <Select value={schemeForm.scheme_type} onValueChange={(v) => setSchemeForm({ ...schemeForm, scheme_type: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="sf-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#6B6B6B]">Duration (months)</Label><Input type="number" value={schemeForm.duration_months} onChange={(e) => setSchemeForm({ ...schemeForm, duration_months: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="sf-duration" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Installment ₹</Label><Input type="number" value={schemeForm.installment_amount} onChange={(e) => setSchemeForm({ ...schemeForm, installment_amount: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="sf-amount" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Bonus Installments</Label><Input type="number" value={schemeForm.bonus_installments} onChange={(e) => setSchemeForm({ ...schemeForm, bonus_installments: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="sf-bonus" /></div>
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">Description</Label><Input value={schemeForm.description} onChange={(e) => setSchemeForm({ ...schemeForm, description: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="sf-desc" /></div>
          </div>
          <DialogFooter><Button onClick={createScheme} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="sf-save">Create Scheme</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enrollment detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{detail.account_no}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-between text-sm">
                <div><span className="text-[#8A8A8A]">Customer:</span> {detail.customer_name}</div>
                <StatusBadge status={detail.status} />
              </div>
              <div className="grid grid-cols-3 gap-2 my-2">
                <div className="bg-[#F8F6F0] rounded-md p-3 text-center"><div className="text-[10px] uppercase text-[#8A8A8A]">Paid</div><div className="font-medium tabular-nums">{rupees(detail.total_paid)}</div></div>
                {detail.scheme_type === "weight_based" ? (
                  <div className="bg-[#F8F6F0] rounded-md p-3 text-center"><div className="text-[10px] uppercase text-[#8A8A8A]">Grams</div><div className="font-medium tabular-nums">{Number(detail.total_grams).toFixed(3)}</div></div>
                ) : (
                  <div className="bg-[#F8F6F0] rounded-md p-3 text-center"><div className="text-[10px] uppercase text-[#8A8A8A]">Value</div><div className="font-medium tabular-nums">{rupees(detail.total_value || detail.total_paid)}</div></div>
                )}
                <div className="bg-[#F8F6F0] rounded-md p-3 text-center"><div className="text-[10px] uppercase text-[#8A8A8A]">Installments</div><div className="font-medium">{detail.paid_count}/{detail.payable_installments || detail.duration_months}</div></div>
              </div>

              {canCollect && detail.status === "active" && (
                <div className="bg-[#FDFBF7] border border-[#E6E2D6] rounded-md p-3 space-y-2">
                  <div className="flex items-end gap-2">
                    <div className="flex-1"><Label className="text-xs text-[#6B6B6B]">Collect Installment ₹</Label><Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pay-amount" /></div>
                    <div className="flex-1"><Label className="text-xs text-[#6B6B6B]">Collection Date</Label><Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pay-date" /></div>
                    <Button onClick={pay} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="collect-btn"><HandCoins size={16} className="mr-1" /> Collect</Button>
                  </div>
                  <Button onClick={postpone} variant="outline" size="sm" className="w-full border-[#E6E2D6] text-[#8A6D2F]" data-testid="postpone-btn">Postpone maturity by 1 month (missed installment)</Button>
                </div>
              )}
              <Button onClick={() => openPdf(`/schemes/enrollments/${detail.id}/acceptance.pdf`)} variant="outline" className="w-full border-[#E6E2D6]" data-testid="acceptance-pdf-btn">Print Acceptance Letter (PDF)</Button>
              {canEditScheme && (
                <div className="bg-[#FDFBF7] border border-[#E6E2D6] rounded-md p-3">
                  <Label className="text-xs text-[#6B6B6B]">Signed Acceptance (upload scanned PDF / image)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="file" accept=".pdf,image/*" onChange={(e) => uploadSigned(e.target.files[0])} className="bg-white border-[#E6E2D6] text-xs" data-testid="signed-upload" />
                    {detail.signed_acceptance && <Button size="sm" variant="outline" className="border-[#E6E2D6] shrink-0" onClick={() => openPdf(`/schemes/enrollments/${detail.id}/signed-acceptance`)} data-testid="signed-view-btn">View</Button>}
                  </div>
                  {detail.signed_acceptance && <div className="text-[11px] text-[#2D6A4F] mt-1">Uploaded: {detail.signed_acceptance.original_filename}</div>}
                </div>
              )}
              {(detail.status === "matured" || detail.status === "redeemed") && (
                <div className="bg-[#E8F5E9] rounded-md p-3 text-sm flex justify-between" data-testid="redeem-balance">
                  <span className="text-[#2D6A4F]">Redeem balance (deduct at POS billing)</span>
                  <span className="font-medium tabular-nums text-[#2D6A4F]">{rupees(detail.redeem_balance || 0)}</span>
                </div>
              )}
              {canCollect && detail.status === "matured" && (
                <Button onClick={() => close("redeem")} className="w-full bg-[#2D6A4F] hover:bg-[#245741] text-white" data-testid="redeem-btn">Mark as Redeemed (balance stays usable at billing)</Button>
              )}
              {canCollect && (detail.status === "active" || detail.status === "matured") && (
                <Button onClick={() => close("cancel")} variant="outline" className="w-full border-[#E6E2D6] text-[#9D2A2A]" data-testid="cancel-scheme-btn">Cancel Scheme</Button>
              )}

              <div className="text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] mt-2">Installment Schedule</div>
              <div className="max-h-52 overflow-y-auto border border-[#E6E2D6] rounded-md">
                <Table>
                  <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Due</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>{detail.installments.map((i) => (
                    <TableRow key={i.index}><TableCell>{i.index}</TableCell><TableCell className="text-xs">{i.due_date.slice(0, 10)}</TableCell><TableCell className="text-right tabular-nums">{rupees(i.amount)}</TableCell><TableCell><StatusBadge status={i.status === "paid" ? "completed" : "pending"} /></TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </div>

              <div className="text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] mt-2">Scheme Ledger</div>
              <div className="max-h-52 overflow-y-auto border border-[#E6E2D6] rounded-md" data-testid="scheme-ledger">
                {(detail.ledger || []).length === 0 ? <EmptyState label="No ledger entries yet" /> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Details</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                    <TableBody>{detail.ledger.map((l) => (
                      <TableRow key={l.id} data-testid={`scheme-ledger-row-${l.id}`}><TableCell className="text-xs">{fmtDate(l.at)}</TableCell><TableCell className="text-xs capitalize">{l.type.replace(/_/g, " ")}</TableCell><TableCell className="text-xs">{l.installment_index ? `Inst #${l.installment_index}` : ""} {l.mode || ""} {l.reference || ""}</TableCell><TableCell className="text-right tabular-nums text-sm">{rupees(l.amount)}</TableCell></TableRow>
                    ))}</TableBody>
                  </Table>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
